import React from 'react';
import { Typography } from '@material-ui/core';
import { FieldError } from '@spike/model';
import { Button } from 'components/UI';
import OptionsButton from 'components/UI/OptionsButton';
import { v4 as uuid } from 'uuid';
import { Option } from '@spike/model';
import {
    AddOnServiceOrderItem,
    CustomOrderItem,
    Order,
    OrderItem,
    OrderizableTypes,
    ProductOrderItem
} from '@spike/order-model';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { ProductsState } from '@spike/products-action';
import { AddOnServicesState } from '@spike/addon-services-action';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Marketplace from '@spike/marketplace-model';
import { useSingleSaleTableStyles } from 'components/Payments/SingleSale/Table/SingleSaleTable.styles';
import { SingleSaleTableList } from 'components/Payments/SingleSale/Table/List/SingleSaleTableList';
import { SingleSaleTableTotal } from 'components/Payments/SingleSale/Table/Total/SingleSaleTableTotal';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import values from 'lodash/values';

type SingleSaleOrderItem = AddOnServiceOrderItem | CustomOrderItem | ProductOrderItem;

interface RemoveOrderItemPayload {
    orderItem: SingleSaleOrderItem;
    nextItems: Order['items'];
}

interface SingleSaleItemsProps {
    order: Order;
    isLoading?: boolean;
    deletingOrderItemId?: number;
    errors?: Array<FieldError>;
    onChange?: (order: Order) => void;
    onRemoveOrderItem?: (payload: RemoveOrderItemPayload) => void;
    onPay?: () => void;
    onSave?: () => void;
}

const orderItemTypeIndex: Record<OrderizableTypes, keyof Order['items']> = {
    [OrderizableTypes.CUSTOM]: 'customs',
    [OrderizableTypes.PRODUCT]: 'products',
    [OrderizableTypes.ADDON_SERVICE]: 'addOnServices',
    [OrderizableTypes.APPOINTMENT]: 'appointments'
};

const DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY = 'default_tax_for_custom_items_id';
const NO_TAX_SETTING_VALUE = '0';

const getDefaultCustomItemsTaxId = (marketplace: Marketplace): number | undefined => {
    const value = marketplace.settings?.find(
        setting => setting.key === DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY
    )?.value;

    if (!value || value === NO_TAX_SETTING_VALUE) {
        return undefined;
    }

    const taxId = Number(value);
    const taxExists = marketplace.taxes.some(tax => Number(tax.id) === taxId);

    return taxExists ? taxId : undefined;
};

const createOrderItem = (type: OrderizableTypes, amount: string): OrderItem => {
    return {
        amount,
        uuid: uuid(),
        quantity: 1,
        total: amount,
        description: '',
        orderizable: { type },
        originalPrice: amount,
        discount: { amount: '0.00' }
    };
};

const createProductOrderItem = (product: Product): ProductOrderItem => {
    return {
        product,
        orderItem: createOrderItem(OrderizableTypes.PRODUCT, product?.price?.toString() || '0.00')
    };
};

const createAddOnServiceOrderItem = (addOnService: AddOnService): AddOnServiceOrderItem => {
    return {
        addOnService,
        orderItem: createOrderItem(OrderizableTypes.ADDON_SERVICE, addOnService?.price?.toString() || '0.00')
    };
};

const createCustomOrderItem = (amount: string, taxId?: number): CustomOrderItem => {
    return {
        orderItem: {
            ...createOrderItem(OrderizableTypes.CUSTOM, amount),
            taxId
        }
    };
};

export const SingleSaleTable: React.FC<SingleSaleItemsProps> = props => {
    const classes = useSingleSaleTableStyles();

    const productState = useSelector<RootState, ProductsState>(state => state.products);
    const addOnServiceState = useSelector<RootState, AddOnServicesState>(state => state.addOnServices);
    const marketplace = useSelector<RootState, Marketplace>(state => state.marketplace.marketplace);

    const totalOrderItemsCount = React.useMemo(
        () => values(props.order.items).reduce((total, value) => total + value.length, 0),
        [props.order]
    );

    const orderItemTypeOptions = React.useMemo<Array<Option<string>>>(() => {
        const options: Array<Option<string>> = [];

        if ((productState.list.page?.length ?? 0) > 0) {
            options.push({
                id: OrderizableTypes.PRODUCT,
                name: 'Product'
            });
        }

        if (addOnServiceState.services.length > 0) {
            options.push({
                id: OrderizableTypes.ADDON_SERVICE,
                name: 'Add-on'
            });
        }

        options.push({
            id: OrderizableTypes.CUSTOM,
            name: 'Custom Item'
        });

        return options;
    }, [productState.list.page, addOnServiceState.services]);

    const addOrderItemHandler = (type: OrderizableTypes) => {
        let orderItemData = undefined;

        if (type === OrderizableTypes.PRODUCT) {
            orderItemData = createProductOrderItem({} as Product);
        } else if (type === OrderizableTypes.ADDON_SERVICE) {
            orderItemData = createAddOnServiceOrderItem({} as AddOnService);
        } else if (type === OrderizableTypes.CUSTOM) {
            orderItemData = createCustomOrderItem('0.00', getDefaultCustomItemsTaxId(marketplace));
        }

        props.onChange &&
            props.onChange({
                ...props.order,
                items: {
                    ...props.order.items,
                    [orderItemTypeIndex[type]]: [...props.order.items[orderItemTypeIndex[type]], orderItemData]
                }
            });
    };

    const changeHandler = (orderItems: Order['items']) => {
        props.onChange && props.onChange({ ...props.order, items: orderItems });
    };

    return (
        <div className={classes.container}>
            <div className={classes.boxTitle}>
                <Typography className={classes.title}>Items</Typography>
                <Typography className={classes.subtitle}>
                    Complete a sale by quickly adding a product, add-on, or custom item.
                </Typography>
            </div>

            <SingleSaleTableList
                order={props.order}
                errors={props.errors}
                deletingOrderItemId={props.deletingOrderItemId}
                onChange={changeHandler}
                onRemoveOrderItem={props.onRemoveOrderItem}
            />

            <div className={classes.optionsButtonContainer}>
                <OptionsButton
                    color="green"
                    label="Add Item"
                    options={orderItemTypeOptions}
                    className={classes.addLineButton}
                    onClick={option => addOrderItemHandler(option.id as OrderizableTypes)}
                />
            </div>

            <div className={classes.boxBottom}>
                <div className={classes.notesSection}>
                    <Textarea
                        key={props.order.client?.id ?? 'no-client'}
                        label="Add a note (visible to client on invoice)"
                        value={props.order.notes ?? ''}
                        onChange={event =>
                            props.onChange?.({
                                ...props.order,
                                notes: event.target.value.slice(0, 500)
                            })
                        }
                        maxLength={500}
                        rows={4}
                    />
                </div>
                <div className={classes.boxPayment}>
                    <SingleSaleTableTotal order={props.order} />
                    {props.onSave ? (
                        <div className={classes.actionsRow}>
                            <Button
                                label="Save"
                                loading={props.isLoading}
                                className={classes.editActionButton}
                                onClick={props.onSave}
                            />
                            <Button
                                label="Pay"
                                loading={props.isLoading}
                                className={classes.editActionButton}
                                onClick={props.onPay}
                                disabled={totalOrderItemsCount === 0}
                            />
                        </div>
                    ) : (
                        <Button
                            label="Pay"
                            loading={props.isLoading}
                            className={classes.payButton}
                            onClick={props.onPay}
                            disabled={totalOrderItemsCount === 0}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
